// ─────────────────────────────────────────────────────────────────────────────
// THE SAME GROUND INSURED TWICE IN ONE CROP YEAR
//
// Kevin, 2026-10-01: "Can we flag or check to see if the 2 fields that were insured to wheat
// for Bridgewater happened to be insured as corn in the spring? Wondering if we could catch a
// scenario where the farmer ripped up wheat without telling us and then planted to a spring
// crop."
//
// A fall crop is drilled the autumn BEFORE its crop year, so a wheat CY2026 acreage report is
// filed in October 2025 and the corn CY2026 report in May 2026 — seven months apart, against
// the same crop year. If the wheat failed over winter and went to corn, the wheat report is
// never revised and both sit in the book claiming the same acres.
//
// ⚠ A CLU CARRYING TWO CROPS IS ORDINARY AND IS NOT THE FINDING. A Common Land Unit is usually
// bigger than a field and growers routinely split one between wheat and corn. Measured
// 2026-10-01 over CY2024+: 1,109 CLU-years carry more than one crop and 350 carry a fall crop
// beside a spring one. Flagging those would be 350 false alarms.
//
// THE FINDING IS THE ACRES. Only when the two together claim MORE than the CLU physically holds
// has the same ground been insured twice — 88 of the 350.
//
// ⚠ AND THE DENOMINATOR WAS CHECKED BEFORE IT WAS TRUSTED. `field_geometries.acres` could have
// been noise; measured over 29,701 single-crop CLU-years it is not — the insured/CLU ratio runs
// p10 0.40, p50 1.00, p90 1.00, p99 1.06, and ONE crop alone exceeds its CLU by >5% on just 332
// (1.1%). A denominator that was wrong a third of the time would have made this whole rule
// worthless, and it would have looked identical from the inside.
//
// ── WHAT THIS RULE REFUSES TO GUESS ─────────────────────────────────────────
//
// 1. IT DOES NOT DECIDE THAT A RIP-UP HAPPENED. It reports that two acreage reports cannot both
//    be right about one piece of ground, which is a fact, and ranks how much is known about why.
//    The answer comes from the grower.
//
// 2. A DECLARED DOUBLE-CROP IS NEVER A FINDING, and the test is RMA's own, not agronomy.
//    Practice code 053 is "Following Another Crop" — 26 soybean coverages in this book carry it.
//    A spring crop written on 053 is insured AS a second crop behind the first and the acres are
//    supposed to double up.
//    ⚠ MEASURED: NOT ONE of the 88 overlapping cells carries it. So the paperwork does not
//    explain any of them, and the 44 wheat→soybeans cases cannot be waved away as ordinary
//    double-cropping — which is what I assumed before checking.
//
// 3. MACHINE EVIDENCE IS PER CLU AND THREE-STATE. If the fall crop was combined on that exact
//    ground that year, it was not ripped up — that closes the case. If the combine was on that
//    CLU for something else and never for the fall crop, that is worth a call. If there is no
//    pass on that CLU at all, the answer is UNKNOWN and must never be printed as "no".
//    ⚠ THE SCOPE IS THE CLU, NOT THE FARM. An earlier cut asked "does this FARM have machine
//    data", which read 15 cases as suggestive; per CLU it is 1. Bridgewater is why — a connected
//    farm with 525 fields still had a wheat harvest whose map never built, so "this farm has JD
//    data" says nothing about whether THIS field was mapped.
//    Measured: fall crop combined 5 · combine there for something else 1 · nothing on the CLU 82.
//
// 4. THE CROP PAIR IS CONTEXT, NOT A VERDICT. Corn cannot follow a July wheat harvest in this
//    geography and soybeans can, so wheat→corn is the pair that cannot be an undeclared
//    double-crop. That is stated as context beside the finding and does not change whether a
//    cell is reported, because the acres are what make it one.
//
// ── PRF ON GROUND THAT WENT TO A ROW CROP ───────────────────────────────────
//
// Kevin, 2026-10-01: "PRF is set in the fall/winter so if a spring insured crop gets planted
// on that acre, that's a problem." PRF sales closing is 1 December for the FOLLOWING crop
// year, so PRF CY2026 is bought in late 2025 and the corn CY2026 goes in the next May —
// against the same crop year, months apart, on what is supposed to be perennial forage.
//
// ⚠ THE PRF ROWS WERE CHECKED FOR BEING AN ARTIFACT BEFORE ANY OF THIS WAS BUILT. PRF is
// rated per 0.25° GRID, not per field, so a policy_fields row for it could easily have been
// the sync listing every CLU the farm operates — in which case every finding here would be
// noise. Measured 2026-10-01 over 74 farm-years: the PRF policy names a SUBSET on 66 of them
// — Red Dawn 52 of 270 CLUs (19%), Jeffres 36 of 749 (5%), Moonrise 27 of 156 (17%). The
// eight at 100% are pure forage operations carrying no other crop policy at all. So the PRF
// acreage report genuinely names ground, and this is a real contradiction when a row crop
// takes it.
//
// ⚠ AND PRF ACREAGE IS USUALLY NULL — 1,031 of 1,302 rows — so the fall/spring acreage test
// cannot simply be reused. Two bases, and each says what it rests on:
//    acres_stated — the PRF row states acres and PRF + spring exceed the CLU. 24 cells carry
//                   a PRF acreage; 7 exceed.
//    whole_field  — PRF states no acres, and the spring crop alone takes 95% or more of the
//                   CLU, so there is no room left for pasture. 27 of the 81 null-acre cells.
// The other 54 null-acre cells leave room and are NOT reported: part of a CLU in pasture and
// part in corn is ordinary, and HAR-GO's 76.6 ac field carrying PRF 20.8 + corn 34 is exactly
// that. Flagging those would be the 350-false-alarm mistake in a new costume.
// ─────────────────────────────────────────────────────────────────────────────
"use strict";

const up = (v) => String(v == null ? "" : v).trim().toUpperCase();
const k = (v) => String(v == null ? "" : v).trim();
const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : null; };
// ⚠ CLU ids arrive in BOTH cases in this book, so every comparison folds first. A naive compare
// reads one CLU as two and the overlap disappears — silently, which is the worst direction.
const cluKey = (v) => String(v == null ? "" : v).trim().toLowerCase();

// Crops drilled in the autumn for the FOLLOWING crop year. Same list the Crop Review's own
// fall lens uses; a crop missing here simply never pairs, which is the safe direction.
const FALL_CROPS = Object.freeze(["WHEAT", "WINTER WHEAT", "BARLEY", "TRITICALE", "RYE", "SPELT"]);
const isFall = (crop) => FALL_CROPS.some((f) => up(crop).indexOf(f) >= 0);
// PRF is neither: it is a RATING PLAN over perennial forage, bought by 1 December for the
// following crop year, and it is not sown or harvested like the crops either side of it.
const isPrf = (crop) => up(crop) === "PRF";
// ⚠ WHAT PRF ACTUALLY INSURES, and it is NOT the same set as api/_jd.js's GRID_FORAGE_CROPS.
// That set exists to decide a UNIT (dry-matter tons) and therefore contains CORN SILAGE and
// SORGHUM SILAGE — annual row crops that happen to be chopped. Growing corn silage on PRF
// ground breaks the perennial sward exactly as grain corn does, so reusing that list would
// have cleared the very thing this rule is looking for.
//
// Found by reading the live output: HARKINS DAIRY came back "a row crop was combined on this
// ground" when what the combine actually saw was ALFALFA — which is what PRF is for.
const PERENNIAL_FORAGE = Object.freeze(["ALFALFA", "GRASS", "CLOVER", "FESCUE", "HAYLAGE",
  "HAY", "PASTURE", "TIMOTHY", "ORCHARDGRASS"]);
// ⚠ THE ANNUAL CROPS ARE EXCLUDED BY NAME FIRST — but NOT for the reason the first version of
// this comment gave. It claimed the exclusion is what separates "CORN SILAGE" from "GRASS
// SILAGE". Measured over all 48 distinct crop names this book carries across `crop_coverage`
// and `jd_field_operations` (2026-10-01): "CORN SILAGE" contains NONE of the words in
// PERENNIAL_FORAGE and reads as a row crop by the substring test alone. The exclusion decided
// nothing on live data — not one of the 48 names carries both an annual word and a forage word.
// The old comment was a plausible story that fit the output and was wrong about the mechanism.
//
// It is kept as a FORWARD guard, and the names it is for are ordinary agronomy an AIP or John
// Deere could ship tomorrow: SORGHUM SUDANGRASS, OAT HAY, RYE HAYLAGE, BARLEY HAY, TRITICALE
// HAYLAGE. Each is an annual whose name carries a forage word, and without the exclusion each
// would clear the very thing this rule looks for. The checker exercises it against THOSE names,
// because CORN SILAGE cannot reach it.
//
// ⚠ AND IT IS INCONSISTENT ON ONE REAL PAIR, WHICH IS LEFT UNRESOLVED ON PURPOSE. This book
// carries `SUDANGRASS` (reads as forage, via GRASS) while `SORGHUM SUDANGRASS` would read as a
// row crop, via the exclusion — two spellings of one crop, two answers. Sudangrass is an ANNUAL
// summer forage, so neither reading is obviously wrong: whether an annual forage standing on PRF
// ground is a problem is a question about RMA's PRF eligibility rules, not about this data.
// It is NOT decided here. `sawCrops` prints the name the combine reported so a reader can see
// it, and the question goes to RMA or to Kevin. Measured: 0 SUDANGRASS passes carry a CLU row
// today, so nothing on screen depends on it yet.
const isPerennialForage = (crop) => {
  const c = up(crop);
  if (!c) return false;
  if (/CORN|SORGHUM|SOYBEAN|WHEAT|BEAN|OAT|BARLEY|RYE|TRITICALE/.test(c)) return false;
  return PERENNIAL_FORAGE.some((f) => c.indexOf(f) >= 0);
};
// ⚠ A PLACEHOLDER IS NOT A CROP, AND READ AS ONE IT PRODUCES THIS PANEL'S LOUDEST SENTENCE
// FROM A FIELD THAT SAYS NOTHING. `jd_field_operations.crop` is the same shape `varietyOf()`
// already documents for `variety` — a column that is 100% non-null and only partly real.
// Measured 2026-10-01 over the 20,563 harvest passes in this book: `NONE` on 292 and
// `UNSPECIFIED_CROP` on 239, so 531 passes whose crop the export does not state.
//
// A placeholder fails the annual test AND the forage test, so `sawRowCrop` goes TRUE and a PRF
// cell prints "a row crop was combined on this ground, which is land insured as perennial
// forage" — the most actionable thing the panel can say — about a pass nobody can name.
//
// LATENT TODAY, AND NOT FIXED BY LUCK: 0 of those 531 carry a `jd_field_ftf` row yet, and that
// table is BUILT from these rows by the CLU backfill, so it is one backfill away. The repo
// already records that backfill stranding 404 operations / 11,871 acres, so "it has not reached
// us" is a statement about the backfill's progress, not about this rule being safe.
//
// `---` is not observed in `crop` but is the documented placeholder in `variety` on the same
// feed, so it is included. Nothing else is guessed at: a name this list does not know still
// reads as a crop, and `sawCrops` prints it, which is how the next placeholder surfaces.
const PLACEHOLDER_CROPS = Object.freeze(["NONE", "UNSPECIFIED_CROP", "---"]);
const isPlaceholderCrop = (crop) => {
  const c = up(crop);
  return !c || PLACEHOLDER_CROPS.indexOf(c) >= 0;
};

// How much of a CLU a row crop must take before there is no room left for pasture on it.
// 95% rather than 100% because an acreage report and an FSA boundary round differently; at
// 95% of a 100-acre field the 5 acres left are headland, not a paddock.
const WHOLE_FIELD = 0.95;

// RMA's own practice for a crop insured as a second crop behind another in the same year.
// Sourced from the book's own `crop_coverage.practice` text — "Following Another Crop" — not
// from a list somebody typed.
const DOUBLE_CROP_PRACTICE = "053";
const DOUBLE_CROP_PRACTICE_NAME = "FOLLOWING ANOTHER CROP";

// ⚠ READ BOTH COLUMNS, BECAUSE THE CODE IS THE SPARSE ONE. Measured over the live
// `crop_coverage` 2026-10-02: `practice_code` is populated on **218 of 2,666 rows (8.2%)**
// while the `practice` TEXT is populated on **2,615 (98.1%)**. Today the two agree exactly —
// the 26 code-053 rows are the identical set to the 26 that read "Following Another Crop", and
// every one of the 2,397 rows carrying text with no code says only "Non-Irrigated" — so this
// changes nothing in the book as it stands.
//
// It is not left alone because of WHICH WAY it fails. A declared double-crop is the one thing
// that EXCUSES a fall+spring cell. Miss it and a grower who did exactly the right thing — wheat
// off in July, soybeans drilled behind it — lands on a call list asking why their ground is
// insured twice. A row arriving with the practice NAME and no code is all it would take, and on
// a column that is blank 92% of the time that is not a remote shape.
//
// The name test is RMA's own practice name and nothing looser: it fires only where the AIP
// itself said so, so it cannot excuse a cell the file never described that way.
const isDeclaredDoubleCrop = (cov) =>
  String(cov && cov.practice_code || "").trim() === DOUBLE_CROP_PRACTICE ||
  up(cov && cov.practice).indexOf(DOUBLE_CROP_PRACTICE_NAME) >= 0;

// ⚠ A CLAIM ON THE FALL CROP IS A DOCUMENTED EXPLANATION — AND IT IS NOT EVIDENCE OF THE SAME
// KIND AS A COMBINE PASS, so it is its own field and not a seventh `EVIDENCE` state. The
// evidence states say what the MACHINE saw; this says what the PAPERWORK says. Conflating them
// would make "the machine was not there" and "the grower filed a claim" the same sentence.
//
// Kevin's ask was a grower who ripped up wheat **without telling us**. A paid or open wheat
// claim on that ground is telling us, so the cell is explained and must not sit in the same
// pile as the ones nobody can account for.
//
// ⚠ IT IS KEYED AT THE FARM, NOT THE POLICY, and that is deliberate. WALTONS WAY carries the
// wheat on `MP-0882675` and the corn on `MP-0912637` — a policy-scoped join would have found
// nothing on the very case that prompted this. (The opposite rule holds for billing credits,
// which settle per policy; this is a different question — "is there a claim for this crop on
// this ground" — and the farm is its grain.)
//
// ⚠ AND `indemnities.crop` AND `.county` ARE BOTH COMMA-JOINED — "CORN, SOYBEANS",
// "LIVINGSTON, MONROE". They are LABELS, never keys. This file already records ~$46,000 of
// credits silently dropped by comparing one with `!==`; both are split here.
const claimKeys = (ind, polFarm) => {
  const m = new Map();
  for (const i of ind || []) {
    const farmerId = polFarm.get(String(i.policy_id));
    if (!farmerId) continue;
    const yr = Number(i.crop_year);
    if (!Number.isFinite(yr)) continue;
    for (const c of String(i.crop || "").split(",")) {
      for (const ct of String(i.county || "").split(",")) {
        const key = `${farmerId}|${yr}|${up(ct)}|${up(c)}`;
        if (!m.has(key)) m.set(key, []);
        m.get(key).push(i);
      }
    }
  }
  return m;
};

// How far over the CLU's own acreage the two crops must claim before this is a finding.
// 5% absorbs two acreage reports rounding differently and an FSA re-measure; it is far too
// tight to absorb a whole second crop.
const OVERLAP_TOL = 1.05;

// ── HOW SURE ARE WE THAT THE ACRES ACTUALLY OVERLAP? ──────────────────────────────────
// Kevin, 2026-10-02: "We just need to make sure the acres are definitely overlapping, and not
// just one CLU that normally gets split and farmed as 2 fields. So we definitely need to know
// its overlapped."
//
// The split-CLU case was already refused — part of a CLU in pasture and part in corn leaves
// room under `own`, so it never becomes a finding (71 PRF+spring cells / 3,220 acres sit in
// that state today and are silent). What was NOT separated is how FAR over the field the two
// reports go, and a 5% relative tolerance is sub-acre on a small field.
//
// ⚠ THE NOISE IS MEASURED, NOT ASSUMED. Over 26,277 CLUs carrying exactly ONE crop — where an
// overlap is impossible by construction, so every difference is pure disagreement between a
// stated acreage report and a measured boundary — the stated figure exceeds the boundary on
// 891 (3%): median 0.29 ac, p90 2.17, p95 4.39. 204 of them clear 1 acre and 96 clear 2. So an
// excess of a few tenths is squarely inside what two sources routinely disagree by, and is not
// evidence of anything.
//
// ⚠ AND A FLAT ACRE FLOOR ALONE IS WRONG — it throws away the STRONGEST cases. Gregory
// Stewart CY2026 is WHEAT 1.1 + SOYBEANS 1.1 on a 1.1-acre CLU: 100% of the field claimed
// twice, 1.1 acres over. A 2-acre floor drops that and keeps nothing better. So either test
// carries a cell on its own:
//    ≥ 2.0 ac  — past the p90 of observed noise in absolute terms, on any size of field
//    ≥ 25%     — four times the p99 of the observed stated/boundary ratio (1.058), so it
//                cannot be rounding however small the field is
// Measured on the live book: 111 certain · 11 probable, an exact partition, and every PRF cell
// is certain. Nine of the eleven are ONE farm whose reports sit consistently a little over its
// boundaries — which is itself the shape this split exists to show rather than hide.
const OVERLAP_MIN_AC = 2.0;
const OVERLAP_MIN_SHARE = 0.25;

// What the machine data can say about a cell. Several states on purpose — `unknown` is the
// common one and it is not a denial.
const EVIDENCE = Object.freeze({
  fallHarvested: "the fall crop was harvested on this ground that year",
  // For PRF this is a CONFIRMATION, not a clearance — see the note by `evidence`.
  rowCropHarvested: "a row crop was harvested on this ground, which is land insured as perennial forage",
  // …and the mirror: the ground grew what PRF insures, so the SPRING acreage report is the
  // half that looks wrong. Same cell, opposite conversation.
  // ⚠ "combined" WAS THE WRONG MACHINE. A combine cuts grain; forage goes through a chopper.
  // Kevin, 2026-10-02: "forage isn't combined, it's harvested." Fixed throughout — "combine"
  // now appears only where the subject really is the machine, as in "no machine pass".
  forageHarvested: "perennial forage was harvested on this ground — consistent with the PRF, so it is the spring acreage report that looks wrong",
  // Fall-shaped wording, and that is correct: for a PRF cell this state needs every named pass
  // to say "PRF", which John Deere never reports. Unreachable on this book's 48 names.
  otherOnly: "a machine was on this ground for another crop and never for the fall crop",
  // ⚠ A PASS EXISTS AND THE EXPORT DOES NOT NAME IT. This state is here because `unknown`'s
  // sentence would otherwise be FALSE for 531 passes in this book — telling a grower "no machine
  // pass on this ground" about ground a combine drove across. See PLACEHOLDER_CROPS.
  unnamedPass: "a machine pass is on this ground that year but the export does not name the crop — nothing here can say either way",
  unknown: "no machine pass on this ground that year — nothing here can say either way",
});

// ⚠ EVERY KEY OF `EVIDENCE` HAS A RANK HERE, AND THE CHECKER ASSERTS THE TWO SETS AGREE.
// `forageHarvested` was added to EVIDENCE without one, and the sort below read its rank through
// a `?? 1` default — so a CONFIRMED fact about the ground ("forage was combined, so it is the
// SPRING report that is wrong") sorted alongside "no machine pass at all" and landed in the
// middle of the list. A silent default is what let that happen, so there is no default now.
//
// Order is the old one exactly — rowCropHarvested < otherOnly < unknown < fallHarvested — with
// the two states that did not exist slotted by what they actually establish:
//   • forageHarvested is confirmed AND actionable, so it sits beside rowCropHarvested;
//   • unnamedPass establishes only that a machine was there, so it sits just above unknown;
//   • fallHarvested stays LAST because it is the least alarming reading — the wheat really was
//     combined, so the overlap is more likely a declared double-crop or a reporting artifact
//     than a grower who tore the wheat up.
const EVIDENCE_RANK = Object.freeze({
  rowCropHarvested: 0,
  forageHarvested: 1,
  otherOnly: 2,
  unnamedPass: 3,
  unknown: 4,
  fallHarvested: 5,
});

/**
 * findDoubleInsured({ policyFields, geometries, coverages, policies, machineRows, cropYear })
 *
 *   policyFields — policy_fields rows (farmer_id, clu_id, crop, crop_year, acres, county, fsn…)
 *   geometries   — field_geometries rows (clu_id, acres) — the CLU's OWN acreage, the denominator
 *   coverages    — crop_coverage rows (policy_id, crop, county, practice_code)
 *   policies     — policies rows (id, farmer_id, crop_year), to resolve a coverage to a farm
 *   machineRows  — jd_field_ftf rows, metric='yield' (clu_id, crop, operation_date)
 *   indemnities  — indemnities rows (policy_id, crop_year, crop, county, status) — a claim on
 *                  the FALL crop is a documented explanation; see `claimKeys`
 *   connections  — jd_connections (farmer_id, jd_org_id, jd_org_name) — ONLY to say whose
 *                  machine produced a pass. Never select the token columns on that table.
 *   cropYear     — optional; omit to scan every year present
 *
 * Nothing here fetches and nothing here decides a claim.
 */
function findDoubleInsured({ policyFields = [], geometries = [], coverages = [], policies = [],
                             machineRows = [], indemnities = [], connections = [],
                             cropYear = null } = {}) {
  const CY = cropYear == null ? null : Number(cropYear);

  // ── the denominator ────────────────────────────────────────────────────────
  const cluAcres = new Map();
  for (const g of geometries) {
    const id = cluKey(g.clu_id);
    const a = n(g.acres);
    if (id && a > 0) cluAcres.set(id, a);
  }

  // ── which (farm, year, county, crop) is insured as a declared double-crop ──
  const polFarm = new Map();
  for (const p of policies) polFarm.set(String(p.id), { farmerId: k(p.farmer_id), year: Number(p.crop_year) });
  // Claims, keyed the same way the declared double-crop set is.
  const claimsBy = claimKeys(indemnities, new Map([...polFarm].map(([k, v]) => [k, v.farmerId])));
  const declaredDouble = new Set();
  for (const c of coverages) {
    if (!isDeclaredDoubleCrop(c)) continue;
    const p = polFarm.get(String(c.policy_id));
    if (!p) continue;
    declaredDouble.add(`${p.farmerId}|${p.year}|${up(c.county)}|${up(c.crop)}`);
  }

  // ⚠ WHOSE MACHINE. `jd_field_ftf.farmer_id` is the CLU's INSURED owner — the attribution is
  // by area overlap — so it is always this cell's farm and says nothing about who drove. The
  // operator is `jd_org_id`, and this maps it back to a farm.
  //
  // It matters because the obvious way to check a finding is to open that grower's John Deere
  // account, and Harkins' stops at 2020 while the alfalfa on his ground is Gary Swede's, dated
  // 2026. A true finding that cannot be checked where a reader will look reads as an invented
  // one. 29 rows; the token columns on jd_connections are never selected.
  const orgFarm = new Map();
  for (const c of connections || []) if (c.jd_org_id != null) orgFarm.set(String(c.jd_org_id), k(c.farmer_id));

  // ── what the combine saw, per CLU per year ────────────────────────────────
  const sawOnClu = new Map();   // "clu|year" -> Set(CROP)
  for (const r of machineRows) {
    if (r.metric && r.metric !== "yield") continue;
    const id = cluKey(r.clu_id);
    if (!id || !r.operation_date) continue;
    const y = Number(String(r.operation_date).slice(0, 4));
    if (!Number.isFinite(y)) continue;
    const key = `${id}|${y}`;
    if (!sawOnClu.has(key)) sawOnClu.set(key, { crops: new Set(), orgs: new Set() });
    sawOnClu.get(key).crops.add(up(r.crop));
    if (r.jd_org_id != null) sawOnClu.get(key).orgs.add(String(r.jd_org_id));
  }

  // ── group the insured fields by (farm, year, CLU) ─────────────────────────
  const cells = new Map();
  for (const f of policyFields) {
    const id = cluKey(f.clu_id);
    if (!id) continue;
    const y = Number(f.crop_year);
    if (CY != null && y !== CY) continue;
    const key = `${k(f.farmer_id)}|${y}|${id}`;
    if (!cells.has(key)) cells.set(key, []);
    cells.get(key).push(f);
  }

  const findings = [], uncheckable = [];
  for (const [key, rows] of cells) {
    const [farmerId, yStr, cluId] = key.split("|");
    const year = Number(yStr);
    const fall = rows.filter((r) => isFall(r.crop));
    const prf = rows.filter((r) => isPrf(r.crop));
    const spring = rows.filter((r) => !isFall(r.crop) && !isPrf(r.crop));
    // A CLU carrying only one of the three has nothing to contradict.
    if (!spring.length || (!fall.length && !prf.length)) continue;

    const own = cluAcres.get(cluId);
    const fallAc = fall.reduce((a, r) => a + (n(r.acres) || 0), 0);
    const springAc = spring.reduce((a, r) => a + (n(r.acres) || 0), 0);

    // ⚠ NO DENOMINATOR, NO FINDING — AND NO SILENCE EITHER. Without the CLU's own acreage the
    // overlap cannot be shown, and dropping the cell would hide a real one behind a missing
    // geometry row. Measured today: 0 of 350 fall+spring cells lack one, so this list is empty
    // and is reported anyway, because the day it is not is the day it matters.
    if (!(own > 0)) {
      uncheckable.push({ farmerId, cropYear: year, cluId,
        county: up(rows[0].county), fsn: k(rows[0].fsn), tract: k(rows[0].tract), fieldId: k(rows[0].field_id),
        fallCrops: [...new Set(fall.map((r) => up(r.crop)))], springCrops: [...new Set(spring.map((r) => up(r.crop)))],
        fallAcres: +fallAc.toFixed(1), springAcres: +springAc.toFixed(1),
        why: "this CLU has no boundary acreage on file, so the two crops cannot be checked against what the ground holds" });
      continue;
    }
    const prfAc = prf.reduce((a, r) => a + (n(r.acres) || 0), 0);
    // ⚠ TWO KINDS, ONE RULE EACH, AND EACH SAYS WHAT IT RESTS ON. A reader who cannot tell
    // 'these two acreage reports exceed the field' from 'the row crop took the whole field and
    // PRF never stated its acres' cannot judge how hard to push the grower.
    const fallOver = fall.length && fallAc + springAc > own * OVERLAP_TOL;
    const prfOver = prf.length && (prfAc > 0
      ? prfAc + springAc > own * OVERLAP_TOL
      // PRF stated no acres — the only thing that can contradict it is a row crop taking the
      // whole field. Anything less leaves room for the pasture and is ordinary.
      : springAc >= own * WHOLE_FIELD);
    if (!fallOver && !prfOver) continue;
    const kind = prfOver ? "prf_spring" : "fall_spring";
    const prfBasis = !prfOver ? null : prfAc > 0 ? "acres_stated" : "whole_field";

    // ── a DECLARED double-crop is not a finding ──────────────────────────────
    // ⚠ IT EXCUSES A FALL CROP AND NEVER PRF. "Following Another Crop" means a second crop
    // behind a first in one season — wheat off in July, soybeans drilled behind it. PRF is not
    // a crop that comes off; it is perennial forage rated per grid, and nothing about a 053
    // practice explains a row crop standing on it. Letting it through here would silence the
    // whole PRF half on any farm that happens to double-crop somewhere.
    const springKeys = [...new Set(spring.map((r) => `${farmerId}|${year}|${up(r.county)}|${up(r.crop)}`))];
    if (kind === "fall_spring" && springKeys.length && springKeys.every((sk) => declaredDouble.has(sk))) continue;

    // ── what the machine can say about THIS ground ──────────────────────────
    const sawRec = sawOnClu.get(`${cluId}|${year}`) || { crops: new Set(), orgs: new Set() };
    const saw = sawRec.crops;
    // ⚠ THE MACHINE SAYS SOMETHING DIFFERENT ABOUT EACH KIND, and the same pass means opposite
    // things. For a FALL crop, a fall harvest CLEARS the cell — the wheat was not ripped up.
    // For PRF, a SPRING-CROP harvest on that ground CONFIRMS it: the combine took a row crop
    // off land insured as perennial forage, which is the contradiction itself rather than
    // evidence against it.
    // A genuine ANNUAL row crop off this ground confirms the contradiction; perennial forage
    // off it says the opposite — the ground grew what PRF insures, so it is the SPRING acreage
    // report that looks wrong, not the PRF. Those need opposite phone calls and must not share
    // a label.
    // ⚠ A PLACEHOLDER PASS IS SET ASIDE BEFORE ANY OF THIS, never read as a crop. It still
    // COUNTS as a machine pass, which is the whole reason `unnamedPass` exists — dropping it
    // would say "no machine pass on this ground" about ground a machine drove across, and the
    // sentences on this panel are what a grower is told.
    const named = [...saw].filter((c) => !isPlaceholderCrop(c));
    const unnamedPasses = saw.size - named.length;
    const sawRowCrop = named.some((c) => !isPrf(c) && !isPerennialForage(c));
    const sawForage = named.some(isPerennialForage);
    const evidence = kind === "prf_spring"
      ? (sawRowCrop ? "rowCropHarvested" : sawForage ? "forageHarvested"
         : named.length ? "otherOnly" : unnamedPasses ? "unnamedPass" : "unknown")
      : (named.some(isFall) ? "fallHarvested"
         : named.length ? "otherOnly" : unnamedPasses ? "unnamedPass" : "unknown");

    // ⚠ A FALL-CROP CLAIM EXPLAINS A TEAR-UP. A PRF CLAIM EXPLAINS NOTHING, AND THAT
    // DISTINCTION IS THE WHOLE OF IT. PRF is a rainfall INDEX plan: it settles on measured
    // precipitation against a grid, whatever is standing on the ground, so a PRF indemnity
    // neither evidences nor requires that the acres were in forage. Measured on the live
    // CY2026 book: 17 of the 53 fall cells are explained by a wheat claim, and SIX PRF cells
    // (MOONRISE x5, MOORLAND) would have been wrongly cleared by a PRF one — on ground that
    // went to corn, a PRF payout is arguably the more troubling fact, not the less.
    // Whose machine, where it was not this farm's. Null when every pass is the insured's own
    // (or when no connection maps the org), because "by someone else" is the exception and
    // printing the farm's own name on every row is noise.
    const otherOrgs = [...sawRec.orgs].map((o) => orgFarm.get(o)).filter((fid) => fid && fid !== farmerId);
    const sawByOthers = otherOrgs.length ? [...new Set(otherOrgs)] : null;

    const fallCrops = [...new Set(fall.map((r) => up(r.crop)))];
    const claimRows = kind !== "fall_spring" ? []
      : [...new Set(fallCrops)].flatMap((c) => claimsBy.get(`${farmerId}|${year}|${up(rows[0].county)}|${c}`) || []);
    // Every status counts — an OPEN claim is still the grower telling us. The status rides
    // along so a reader can tell a settled explanation from one still in adjustment.
    const fallClaim = claimRows.length ? {
      crops: [...new Set(claimRows.map((i) => up(i.crop)))].sort(),
      statuses: [...new Set(claimRows.map((i) => k(i.status)).filter(Boolean))].sort(),
      amount: +claimRows.reduce((a, i) => a + (n(i.indemnity_amount) || 0), 0).toFixed(2),
    } : null;
    const springCrops = [...new Set(spring.map((r) => up(r.crop)))].sort();
    // Hoisted so the certainty test below reads the SAME number the row prints. Computing it
    // twice is how two surfaces come to disagree about one cell.
    const overAc = prfBasis === "whole_field" ? +own.toFixed(1)
      : kind === "prf_spring" ? +(prfAc + springAc - own).toFixed(1)
      : +(fallAc + springAc - own).toFixed(1);
    // ⚠ HOW SURE WE ARE THAT THE ACRES OVERLAP — see OVERLAP_MIN_AC / OVERLAP_MIN_SHARE above.
    // Either test carries a cell alone, because an absolute floor alone discards 100% of a
    // small field and a relative one alone discards a large overlap on a large field.
    //
    // ⚠ A WHOLE-FIELD PRF CELL NEEDS NO BRANCH OF ITS OWN, and it had one until mutation
    // testing showed the mutation could not be detected. Its `overAc` IS the field, so its
    // share is 100% and the share test already makes it certain — a special case that can
    // never change the answer is a test nobody can fail, and this file has paid for one of
    // those already. Deleting it is also the more correct reading at the degenerate end: a
    // CLU measuring a few hundredths of an acre is noise whatever covers it, and the live
    // whole-field cells run 3.6 to 105.3 acres, so nothing in the book moves.
    const overShare = own > 0 ? overAc / own : 0;
    const overlapClass = (overAc >= OVERLAP_MIN_AC || overShare >= OVERLAP_MIN_SHARE)
      ? "certain" : "probable";
    findings.push({
      kind, prfBasis,
      farmerId, cropYear: year, cluId,
      county: up(rows[0].county), fsn: k(rows[0].fsn), tract: k(rows[0].tract), fieldId: k(rows[0].field_id),
      cluAcres: +own.toFixed(1),
      fallCrops, springCrops,
      // PRF acres are usually NULL and null must survive the rounding — `(0).toFixed(1)` would
      // turn "the report does not say" into "zero acres", which is the blank-is-not-a-zero rule
      // this codebase applies to every other input.
      prfAcres: prf.length ? (prfAc > 0 ? +prfAc.toFixed(1) : null) : undefined,
      fallAcres: +fallAc.toFixed(1), springAcres: +springAc.toFixed(1),
      // ⚠ WHAT IS "OVER" DEPENDS ON THE KIND, and the first cut printed nonsense. A whole-field
      // PRF cell has fallAc 0 and prfAc null, so `fall + spring - own` came out at 0 or NEGATIVE
      // — "over -0.8" on a finding — and the sort by it put every one of them together at the
      // bottom. Read against the live book, not reasoned about.
      //
      // Where both sides state acres the overlap is arithmetic. Where PRF states none, there is
      // no overlap to compute: the fact is that the row crop took the whole field, so the acres
      // AT STAKE are the field itself and the share is what says how completely.
      overAcres: overAc,
      // null unless it is the whole-field basis, where it is the whole point.
      springShare: prfBasis === "whole_field" ? Math.round(100 * springAc / own) : null,
      // ⚠ HOW SURE, AND WHY — a reader must be able to tell "these acres are definitely on top
      // of each other" from "they might be, within what two sources routinely disagree by".
      overlapClass,
      overlapShare: Math.round(100 * overShare),
      overlapWhy: prfBasis === "whole_field"
        ? "the row crop covers the whole field and PRF states no acres, so no room is left for forage"
        : overlapClass === "certain"
          ? `the two reports exceed the field by ${overAc} ac, ${Math.round(100 * overShare)}% of it`
          : `the two reports exceed the field by only ${overAc} ac (${Math.round(100 * overShare)}% of it) — inside what a stated acreage report and a measured boundary routinely differ by`,
      // Say which arithmetic produced `overAcres`, so a reader is never guessing which it is.
      overBasis: prfBasis === "whole_field" ? "the field itself — the row crop takes it all, and PRF stated no acres"
        : "the two acreage reports together, less what the field holds",
      // null unless the paperwork already accounts for this cell — see `claimKeys`.
      fallClaim,
      evidence, evidenceSay: EVIDENCE[evidence],
      sawCrops: [...saw].sort(),
      // farmer ids, resolved to names by the caller — see `sawByOthers` above.
      sawByOthers,
      // How many of those passes the export could not name. Reported rather than folded into
      // the evidence label, so "a machine was here and we cannot say what for" is a countable
      // fact and not an inference a reader has to make from a missing crop name.
      unnamedPasses,
      // ⚠ CONTEXT, NOT A VERDICT. Soybeans can follow a July wheat harvest; corn cannot. It does
      // not decide whether this is reported — the acres do — and it is never the whole answer,
      // because an undeclared double-crop is still an acreage report that needs correcting.
      //
      // ⚠ AND IT IS NULL ON A PRF CELL, because there is no fall crop for anything to follow.
      // Computed for every kind it rendered "cannot follow a fall crop in one year" under a PRF
      // finding — a sentence about a crop that is not in the finding — and counted that cell into
      // `cannotDoubleCrop`. Null rather than false: false would read as "it cannot follow", which
      // is a claim, where the truth is that the question does not apply.
      springCanFollowFall: kind === "fall_spring" ? springCrops.every((c) => /SOYBEAN/.test(c)) : null,
      // The one thing the paperwork could have said and did not.
      declaredDoubleCrop: false,
    });
  }

  const by = (e) => findings.filter((f) => f.evidence === e).length;
  findings.sort((a, b) =>
    // What is known first: the cases the combine speaks to, then the rest by how much ground is
    // double-claimed — acres are the thing that makes any of these worth a call.
    // rowCropHarvested and forageHarvested are the two states that CONFIRM a contradiction
    // rather than merely failing to rule one out, so they lead. EVIDENCE_RANK is read with NO
    // default — an unranked state throws in the checker rather than sorting into the middle.
    // ⚠ A CELL THE PAPERWORK EXPLAINS SORTS LAST, whatever its acres or its machine evidence.
    // It is still listed — the acreage reports do still contradict — but it is not the call to
    // make first, and burying the unexplained ones under it is how a list stops being read.
    ((a.fallClaim ? 1 : 0) - (b.fallClaim ? 1 : 0))
    || (EVIDENCE_RANK[a.evidence] - EVIDENCE_RANK[b.evidence])
    || (b.overAcres - a.overAcres));

  return {
    cropYear: CY,
    findings,
    totals: {
      cells: findings.length,
      acres: +findings.reduce((s, f) => s + f.overAcres, 0).toFixed(1),
      farms: new Set(findings.map((f) => f.farmerId)).size,
      // ⚠ CERTAINTY PARTITIONS `cells` EXACTLY, same contract as the evidence states below.
      // A cell in neither silently shortens the list; a cell in both double-counts a farm.
      certain: findings.filter((f) => f.overlapClass === "certain").length,
      probable: findings.filter((f) => f.overlapClass === "probable").length,
      // ⚠ THE EVIDENCE STATES ARE COUNTED APART AND THEY MUST PARTITION `cells` EXACTLY.
      // `unknown` is the big one and saying so is the point — a tool that reported 88 findings
      // without saying 82 of them are unknown would be claiming to know something it does not.
      //
      // This was FOUR hand-written buckets and `forageHarvested` was not one of them, so every
      // PRF cell where the combine took forage was inside `cells` and inside no bucket: the
      // four printed counts summed to LESS than the headline, with nothing to say which were
      // missing. Same shape as the partition invariant `check-election-rules` guards — a row in
      // no state silently shortens the list, a row in two double-counts a farm. Derived from
      // `EVIDENCE` now, so a state added later is counted the moment it exists, and the checker
      // asserts the sum against `cells`.
      ...Object.fromEntries(Object.keys(EVIDENCE).map((e) => [e, by(e)])),
      // The two kinds are counted apart because they are different conversations with the
      // grower: one asks whether a wheat crop failed, the other whether forage ground was
      // broken up after a PRF policy was bought on it.
      fallSpring: findings.filter((f) => f.kind === "fall_spring").length,
      prfSpring: findings.filter((f) => f.kind === "prf_spring").length,
      // Explained by a claim on the fall crop — reported, never silently dropped. A cell the
      // paperwork accounts for is not the thing this tool is looking for, but hiding it would
      // make the headline disagree with the list.
      explainedByClaim: findings.filter((f) => f.fallClaim).length,
      // Scoped to the kind the question applies to — a PRF cell has no fall crop to follow, and
      // `!null` is true, so an unscoped filter counted every PRF/corn cell as an impossible
      // double-crop.
      cannotDoubleCrop: findings.filter((f) => f.kind === "fall_spring" && !f.springCanFollowFall).length,
    },
    uncheckable,
    uncheckableCount: uncheckable.length,
  };
}

// What this rule reads, so a caller's query can be checked against it — the dead-guard class
// `check-query-contract` exists for. `practice_code` is the easy one to leave out: omitted,
// every declared double-crop becomes a false finding and the list silently grows.
const NEEDS_COLUMNS = Object.freeze({
  policy_fields: ["farmer_id", "clu_id", "crop", "crop_year", "acres", "county", "fsn", "tract", "field_id"],
  field_geometries: ["clu_id", "acres"],
  crop_coverage: ["policy_id", "crop", "county", "practice_code", "practice"],
  policies: ["id", "farmer_id", "crop_year"],
  jd_field_ftf: ["clu_id", "crop", "metric", "operation_date", "jd_org_id", "jd_operation_id"],
  // ⚠ THESE THREE COLUMNS ONLY. jd_connections also holds access_token / refresh_token.
  jd_connections: ["farmer_id", "jd_org_id", "jd_org_name"],
  indemnities: ["policy_id", "crop_year", "crop", "county", "status", "indemnity_amount"],
});

module.exports = { findDoubleInsured, isFall, isPrf, isPerennialForage, isPlaceholderCrop,
                   FALL_CROPS, PERENNIAL_FORAGE, PLACEHOLDER_CROPS,
                   DOUBLE_CROP_PRACTICE, DOUBLE_CROP_PRACTICE_NAME, isDeclaredDoubleCrop, OVERLAP_TOL, WHOLE_FIELD, OVERLAP_MIN_AC, OVERLAP_MIN_SHARE, EVIDENCE, EVIDENCE_RANK,
                   NEEDS_COLUMNS };
